/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {AspectRatio, Box, Badge, Image} from '@salesforce/retail-react-app/app/components/shared/ui'

import {FormattedMessage, useIntl} from 'react-intl'

import {useItemVariant} from '@salesforce/retail-react-app/app/components/item-variant'
import {remCalc} from '../../theme/_utils'
import {findImageGroupBy} from '../../utils/image-groups-utils'

/**
 * In the context of a cart product item variant, this component renders the item's
 * main image.
 *
 * @todo = This component will render a `SALE` badge when the qualifier is available
 * on the custom property `c_inPromo`. This will need to be expanded upon to handle
 * different badge/qualifiers and property names.
 */
const ItemImage = ({imageProps, boxWidth = remCalc(84), ratio = 1, ...props}) => {
    const variant = useItemVariant()
    const {formatMessage} = useIntl()

    // We find the 'small' images in the variant's image groups based on variationValues and pick the first one
    const image =
        variant?.c_isOfficina === 'true' && variant?.c_officinaConfigurationImage
            ? {
                  link: variant.c_officinaConfigurationImage,
                  alt: variant?.name,
              }
            : findImageGroupBy(variant?.imageGroups, {
                  viewType: 'large',
                  selectedVariationAttributes: variant?.variationValues,
              })?.images?.[0]

    return (
        <Box width={boxWidth} {...props}>
            <AspectRatio ratio={ratio}>
                <Box position="relative">
                    {variant.isProductUnavailable && (
                        <Badge
                            position="absolute"
                            top={0}
                            left={0}
                            marginLeft={2}
                            marginTop={2}
                            fontSize="10px"
                            variant="solid"
                            colorScheme="red"
                            role="status"
                            aria-label={formatMessage({
                                id: 'item_image.badge.unavailable.label',
                                defaultMessage: 'Product is currently unavailable',
                            })}
                        >
                            <FormattedMessage
                                defaultMessage="Unavailable"
                                id="item_image.label.unavailable"
                                description="A unavailable badge placed on top of a product image"
                            />
                        </Badge>
                    )}

                    {image && (
                        <Image
                            role="img"
                            alt={
                                image.alt ||
                                formatMessage({
                                    id: 'item_image.alt.fallback',
                                    defaultMessage: `Product image `,
                                })
                            }
                            src={`${image.disBaseLink || image.link}`}
                            ignoreFallback={true}
                            {...imageProps}
                        />
                    )}
                </Box>
            </AspectRatio>
        </Box>
    )
}

ItemImage.propTypes = {
    imageProps: PropTypes.object,
    ratio: PropTypes.number,
    boxWidth: PropTypes.string,
}

export default ItemImage
